<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  Ban,
  ChevronDown,
  CircleSlash,
  History,
  LoaderCircle,
  Play,
  RotateCcw,
  UserCheck,
  Users,
  X,
} from "@lucide/vue";
import { api } from "@/lib/api";
import type {
  MobileGenerationRecord,
  MobileGenerationStatus,
  MobileUser,
  MobileUserStatus,
} from "@/types";

const users = ref<MobileUser[]>([]);
const loading = ref(true);
const error = ref("");
const savingId = ref("");
const quotaDrafts = ref<Record<string, string>>({});
const expandedUserId = ref("");
const loadingRecordsId = ref("");
const recordError = ref("");
const recordsByUser = ref<Record<string, MobileGenerationRecord[] | undefined>>({});

const statusLabels: Record<MobileUserStatus, string> = {
  pending: "待审核",
  approved: "已通过",
  rejected: "已拒绝",
  banned: "已封禁",
};

const generationStatusLabels: Record<MobileGenerationStatus, string> = {
  processing: "生成中",
  succeeded: "已成功",
  failed: "失败",
};

const pendingCount = computed(
  () => users.value.filter((user) => user.status === "pending").length,
);

function formatTime(value?: string) {
  return value ? value.slice(0, 16).replace("T", " ") : "-";
}

async function load() {
  error.value = "";
  try {
    users.value = (await api.mobileUsers()).users;
    quotaDrafts.value = Object.fromEntries(
      users.value.map((user) => [user.id, String(user.quota)]),
    );
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "申请列表加载失败";
  } finally {
    loading.value = false;
  }
}

async function patchUser(user: MobileUser, payload: { status?: MobileUserStatus; quota?: number }) {
  error.value = "";
  savingId.value = user.id;
  try {
    const updated = (await api.updateMobileUser(user.id, payload)).user;
    users.value = users.value.map((item) => (item.id === updated.id ? updated : item));
    quotaDrafts.value[user.id] = String(updated.quota);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "操作失败";
  } finally {
    savingId.value = "";
  }
}

async function saveQuota(user: MobileUser) {
  const quota = Number(quotaDrafts.value[user.id]);
  if (!Number.isInteger(quota) || quota < 0 || quota > 9999) {
    error.value = "每日生成上限需为 0–9999 的整数。";
    return;
  }
  if (quota === user.quota) return;
  await patchUser(user, { quota });
}

async function toggleRecords(user: MobileUser) {
  if (expandedUserId.value === user.id) {
    expandedUserId.value = "";
    return;
  }
  expandedUserId.value = user.id;
  recordError.value = "";
  if (recordsByUser.value[user.id] !== undefined) return;
  loadingRecordsId.value = user.id;
  try {
    recordsByUser.value[user.id] = (
      await api.mobileGenerationRecords(user.id)
    ).records;
  } catch (cause) {
    recordError.value = cause instanceof Error ? cause.message : "生成记录加载失败";
  } finally {
    loadingRecordsId.value = "";
  }
}

onMounted(load);
</script>

<template>
  <div class="page-stack">
    <section class="catalog-toolbar panel">
      <div class="filter-tabs">
        <Users :size="16" />
        <strong>移动端注册申请</strong>
      </div>
      <span class="catalog-count">
        共 {{ users.length }} 个账号 · 待审核 {{ pendingCount }} · 默认每天 2 次生成
      </span>
    </section>

    <section v-if="error" class="error-banner">
      <span>{{ error }}</span>
    </section>

    <section v-if="loading" class="empty-catalog panel">
      <h2>正在加载申请列表…</h2>
    </section>

    <section v-else-if="!users.length" class="empty-catalog panel">
      <span><Users :size="32" /></span>
      <h2>还没有移动端注册申请</h2>
      <p>移动端访问 /m 页面注册后，申请会出现在这里，审核通过即可登录创作。</p>
    </section>

    <section v-else class="applicant-table panel">
      <table>
        <thead>
          <tr>
            <th>用户名</th>
            <th>状态</th>
            <th>今日 / 每日上限</th>
            <th>累计生成</th>
            <th>申请时间</th>
            <th>最近登录</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="user in users" :key="user.id">
            <tr :class="{ 'record-row-open': expandedUserId === user.id }">
              <td class="applicant-name">{{ user.username }}</td>
              <td>
                <span class="applicant-status" :class="`status-${user.status}`">
                  {{ statusLabels[user.status] }}
                </span>
              </td>
              <td>
                <div class="applicant-quota">
                  <span><strong>{{ user.dailyUsed }}</strong> /</span>
                  <input
                    v-model="quotaDrafts[user.id]"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    max="9999"
                    :aria-label="`${user.username} 的每日生成上限`"
                  />
                  <button
                    class="button button-ghost button-sm"
                    :disabled="savingId === user.id || Number(quotaDrafts[user.id]) === user.quota"
                    @click="saveQuota(user)"
                  >
                    保存
                  </button>
                </div>
              </td>
              <td>
                <button
                  class="applicant-total"
                  type="button"
                  :aria-expanded="expandedUserId === user.id"
                  :aria-controls="`generation-records-${user.id}`"
                  @click="toggleRecords(user)"
                >
                  <History :size="15" />
                  <strong>{{ user.used }}</strong> 次
                  <ChevronDown :size="15" :class="{ rotated: expandedUserId === user.id }" />
                </button>
              </td>
              <td class="applicant-time">{{ formatTime(user.createdAt) }}</td>
              <td class="applicant-time">{{ formatTime(user.lastLoginAt) }}</td>
              <td>
                <div class="applicant-actions">
                  <template v-if="user.status === 'pending'">
                    <button class="button button-lime button-sm" :disabled="savingId === user.id" @click="patchUser(user, { status: 'approved' })">
                      <UserCheck :size="15" />通过
                    </button>
                    <button class="button button-ghost button-sm" :disabled="savingId === user.id" @click="patchUser(user, { status: 'rejected' })">
                      <X :size="15" />拒绝
                    </button>
                  </template>
                  <template v-else-if="user.status === 'approved'">
                    <button class="button button-ghost button-sm applicant-ban" :disabled="savingId === user.id" @click="patchUser(user, { status: 'banned' })">
                      <Ban :size="15" />封禁
                    </button>
                  </template>
                  <template v-else-if="user.status === 'rejected'">
                    <button class="button button-lime button-sm" :disabled="savingId === user.id" @click="patchUser(user, { status: 'approved' })">
                      <UserCheck :size="15" />通过
                    </button>
                  </template>
                  <template v-else-if="user.status === 'banned'">
                    <button class="button button-lime button-sm" :disabled="savingId === user.id" @click="patchUser(user, { status: 'approved' })">
                      <RotateCcw :size="15" />解封
                    </button>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="expandedUserId === user.id" class="generation-record-row">
              <td :id="`generation-records-${user.id}`" colspan="7">
                <section class="generation-record-panel" :aria-label="`${user.username} 的生成记录`">
                  <header>
                    <div>
                      <History :size="17" />
                      <strong>{{ user.username }} 的生成记录</strong>
                    </div>
                    <span>累计成功 {{ user.used }} 次</span>
                  </header>

                  <div v-if="loadingRecordsId === user.id" class="generation-record-state">
                    <LoaderCircle class="spin" :size="18" />正在加载记录…
                  </div>
                  <div v-else-if="recordError" class="generation-record-state is-error">
                    {{ recordError }}
                  </div>
                  <div v-else-if="!recordsByUser[user.id]?.length" class="generation-record-state">
                    暂无可查看的生成明细；本功能上线前的累计次数仍保留在统计中。
                  </div>
                  <div v-else class="generation-record-list">
                    <article
                      v-for="record in recordsByUser[user.id]"
                      :key="record.id"
                      class="generation-record-item"
                    >
                      <div class="generation-record-title">
                        <strong>{{ record.title || "未命名作品" }}</strong>
                        <span class="generation-status" :class="`status-${record.status}`">
                          {{ generationStatusLabels[record.status] }}
                        </span>
                      </div>
                      <p>{{ record.prompt || "未填写音乐描述" }}</p>
                      <div class="generation-record-meta">
                        <span>{{ formatTime(record.completedAt || record.createdAt) }}</span>
                        <span v-if="record.status === 'failed' && record.errorMessage">
                          {{ record.errorMessage }}
                        </span>
                        <router-link
                          v-if="record.status === 'succeeded' && record.trackId"
                          class="button button-ghost button-sm"
                          :to="`/listen/${record.trackId}`"
                        >
                          <Play :size="14" />试听
                        </router-link>
                      </div>
                    </article>
                  </div>
                </section>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <p class="applicant-note">
        <CircleSlash :size="14" />
        每日额度按北京时间 00:00 自动重置；累计次数不会清零。封禁后账号立即无法登录和生成。
      </p>
    </section>
  </div>
</template>
